import React from "react";
import styled from "styled-components";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Mail } from "lucide-react";

import RoleView from "./views/RoleView";
import AboutView from "./views/AboutView";
import VideosView from "./views/VideosView";
import TypeView from "./views/TypeView";
import ProjectView from "./views/ProjectView";
import LyricsView from "./views/LyricsView";
import OverviewView from "./views/OverviewView";
import VerticalSpace from "./components/VerticalSpace";
import Navbar from "./components/Navbar";

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Page = styled.div`
  padding: 60px;

  @media (max-width: 1200px) {
    padding: 30px;
  }

  @media (max-width: 650px) {
    padding: 30px 20px;
  }
`;

const Hero = styled(Page)`
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #fff 0%, #fafbfc 100%);
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 20px 0px,
    rgba(0, 0, 0, 0.05) 0px 1px 5px 0px;
  border-bottom: 1px solid #e1e5e9;
  position: relative;
  overflow: hidden;
  padding: 40px 60px 30px 60px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.8) 50%,
      transparent 100%
    );
  }

  & > * ~ * {
    margin: 35px 0 0 0;
  }

  @media (max-width: 1200px) {
    padding: 30px;
  }

  @media (max-width: 650px) {
    align-items: center;
    padding: 25px 20px 20px 20px;
    & > * ~ * {
      margin: 25px 0 0 0;
    }
  }
`;

const Footer = styled.footer`
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.15) 2px 1px 10px 1px;
  border-top: 1px solid #ddd;
  padding: 40px 60px;

  @media (max-width: 1200px) {
    padding: 30px;
  }

  @media (max-width: 650px) {
    padding: 30px 20px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const FooterTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FooterTitle = styled.h3`
  font-family: "Muli", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 0.5px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
    box-shadow: rgba(0, 0, 0, 0.15) 0 4px 12px;
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #eee;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  margin: 0;
  color: #888;
  font-size: 14px;
`;

const FooterNav = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FooterNavLink = styled.a`
  color: #666;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  position: relative;

  @media (max-width: 900px) {
    gap: 30px;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const ProfileImage = styled.img`
  display: block;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  overflow: hidden;
  object-fit: cover;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 8px 30px 0px,
    rgba(0, 0, 0, 0.1) 0px 3px 10px 0px;
  border: 3px solid #fff;
  transition: all 0.4s ease;
  position: relative;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.02);
    box-shadow: rgba(0, 0, 0, 0.2) 0px 12px 40px 0px,
      rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;
  }

  &::after {
    content: "";
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border-radius: 50%;
    background: linear-gradient(
      45deg,
      ${({ theme }) => theme.primary}20,
      transparent 60%
    );
    z-index: -1;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 650px) {
    width: 120px;
    height: 120px;
  }
`;

const PageTitle = styled.h1`
  display: inline-flex;
  align-items: center;
  height: 65px;
  font-size: 28px;
  font-family: "Muli", sans-serif;
  text-transform: uppercase;
  padding: 0 18px;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 300ms ease;
  position: relative;
  letter-spacing: 1px;
  flex-shrink: 0;

  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  color: ${({ theme }) => theme.primary};
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  box-shadow: rgba(0, 0, 0, 0.08) 0px 2px 8px 0px,
    rgba(0, 0, 0, 0.04) 0px 1px 3px 0px;

  &:hover {
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary} 0%,
      ${({ theme }) => theme.primary}dd 100%
    );
    border-color: ${({ theme }) => theme.primary};
    color: #fff;
    transform: translateY(-2px);
    box-shadow: rgba(0, 0, 0, 0.15) 0px 8px 25px 0px,
      rgba(0, 0, 0, 0.1) 0px 3px 10px 0px;

    span {
      background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
      border-color: #e1e5e9;
      color: ${({ theme }) => theme.primary};
      box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 15px 0px,
        rgba(0, 0, 0, 0.05) 0px 2px 5px 0px;
    }
  }

  &:active {
    transform: translateY(0);
  }

  span {
    font-family: "Permanent Marker";
    font-weight: normal;
    display: flex;
    align-items: center;
    padding: 0 18px;
    margin-left: 16px;
    font-size: 36px;
    height: 80px;
    transition: all 300ms ease;
    border: 1px solid transparent;
    border-radius: 6px;
    position: relative;

    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary} 0%,
      ${({ theme }) => theme.primary}dd 100%
    );
    color: white;
    box-shadow: rgba(0, 0, 0, 0.12) 0px 4px 12px 0px,
      rgba(0, 0, 0, 0.08) 0px 2px 6px 0px;
  }

  @media (max-width: 900px) {
    font-size: 22px;
    height: 50px;
    padding: 0 14px;

    span {
      height: 60px;
      font-size: 28px;
      padding: 0 14px;
      margin-left: 12px;
    }
  }

  @media (max-width: 650px) {
    font-size: 20px;
    height: 45px;
    padding: 0 12px;

    span {
      height: 55px;
      font-size: 26px;
      padding: 0 12px;
      margin-left: 10px;
    }
  }

  @media (max-width: 375px) {
    font-size: 18px;
    height: 40px;
    padding: 0 10px;

    span {
      font-size: 22px;
      height: 50px;
      padding: 0 10px;
      margin-left: 8px;
    }
  }
`;

const App = () => {
  const navigate = useNavigate();

  const handlePageTitleClick = () => {
    navigate("/");
  };

  return (
    <AppContainer>
      <Hero>
        <Row>
          <ProfileImage alt="Maria Guttesen" src="/mariaguttesen.jpg" />
          <PageTitle onClick={handlePageTitleClick}>
            Maria Guttesen <span>Music</span>
          </PageTitle>
        </Row>
        <Navbar />
      </Hero>
      <Page>
        <Routes>
          <Route path="/about/:lang" element={<AboutView />} />
          <Route path="/about" element={<Navigate to="/about/en" replace />} />
          <Route path="/videos" element={<VideosView />} />
          <Route path="/role/:roleName" element={<RoleView />} />
          <Route path="/type/:typeName" element={<TypeView />} />
          <Route path="/project/:projectId" element={<ProjectView />} />
          <Route path="/song/:songId/lyrics" element={<LyricsView />} />
          <Route path="/" element={<OverviewView />} />
        </Routes>
      </Page>
      <VerticalSpace $amount={100} />
      <Footer>
        <FooterContent>
          <FooterTop>
            <FooterSection>
              <FooterTitle>Connect</FooterTitle>
              <SocialLinks>
                <SocialLink
                  href="https://open.spotify.com/artist/4IvxxyX8ewKoKaB0hlauUn"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Spotify"
                >
                  <img src="/images/spotify-icon.png" alt="Spotify" />
                </SocialLink>
                <SocialLink
                  href="https://music.apple.com/artist/maria-guttesen/4854676"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Apple Music"
                >
                  <img src="/images/apple-music-icon.svg" alt="Apple Music" />
                </SocialLink>
                <SocialLink
                  href="https://www.youtube.com/channel/UCuNXT42l7LlWDKyYLRzqdEQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="YouTube"
                >
                  <img src="/images/youtube-icon.png" alt="YouTube" />
                </SocialLink>
                <SocialLink
                  href="https://soundcloud.com/mariaguttesen"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="SoundCloud"
                >
                  <img src="/images/soundcloud-icon.png" alt="SoundCloud" />
                </SocialLink>
                <SocialLink
                  href="https://www.instagram.com/musikskaberig"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                >
                  <img src="/images/instagram-icon.png" alt="Instagram" />
                </SocialLink>
              </SocialLinks>
            </FooterSection>

            <FooterSection>
              <FooterTitle>Contact</FooterTitle>
              <ContactInfo>
                <ContactLink href="mailto:infomgroots@gmail.com">
                  <Mail color="currentColor" size={16} />
                  infomgroots@gmail.com
                </ContactLink>
              </ContactInfo>
            </FooterSection>
          </FooterTop>

          <FooterBottom>
            <Copyright>
              © {new Date().getFullYear()} Maria Guttesen. All rights reserved.
            </Copyright>
            <FooterNav>
              <FooterNavLink onClick={() => navigate("/")}>Home</FooterNavLink>
              <FooterNavLink onClick={() => navigate("/about/en")}>
                About
              </FooterNavLink>
              <FooterNavLink onClick={() => navigate("/videos")}>
                Videos
              </FooterNavLink>
            </FooterNav>
          </FooterBottom>
        </FooterContent>
      </Footer>
    </AppContainer>
  );
};

export default App;
